import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Card, Field } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const blank = () => ({ title: "" });

const TemplateDialog = ({ open, onOpenChange, template, types, onSaved }) => {
  const [f, setF] = useState({ name: "", project_type: "", items: [blank()] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(template ? { name: template.name, project_type: template.project_type, items: template.items.map((i) => ({ title: i.title })) } : { name: "", project_type: types[0] || "", items: [blank()] }); }, [open, template, types]);
  const setItem = (i, k, v) => setF((x) => ({ ...x, items: x.items.map((it, j) => (j === i ? { ...it, [k]: v } : it)) }));
  const move = (i, d) => setF((x) => { const items = [...x.items]; const [it] = items.splice(i, 1); items.splice(i + d, 0, it); return { ...x, items }; });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, items: f.items.filter((i) => i.title.trim()).map((i) => ({ title: i.title.trim() })) };
      if (template) await api.put(`/checklist-templates/${template.id}`, body); else await api.post("/checklist-templates", body);
      toast.success(template ? "Template updated (existing projects unchanged)" : "Template created");
      onSaved(); onOpenChange(false);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
        <DialogHeader><DialogTitle>{template ? "Edit template" : "New checklist template"}</DialogTitle><DialogDescription>A template is just task names in order. Applying it adds unchecked tasks to a project; editing a template never changes projects that already used it.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-1">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Template name" required><Input data-testid="tpl-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
            <Field label="Project type" required>
              <Select value={f.project_type} onValueChange={(v) => setF({ ...f, project_type: v })}>
                <SelectTrigger data-testid="tpl-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{[...new Set([...types, f.project_type].filter(Boolean))].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <div>
            <div className="grid grid-cols-[24px_1fr_60px] gap-2 text-[11px] font-medium text-slate-500 mb-1 px-1"><span>No.</span><span>Task / Deliverable</span><span /></div>
            <div className="space-y-1.5">
              {f.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[24px_1fr_60px] gap-2 items-center" data-testid={`tpl-item-${i}`}>
                  <span className="text-xs text-slate-400 text-center">{i + 1}</span>
                  <Input data-testid={`tpl-item-title-${i}`} value={it.title} onChange={(e) => setItem(i, "title", e.target.value)} placeholder="Task" className="h-8 text-sm" />
                  <div className="flex items-center gap-1 text-slate-400">
                    <button type="button" disabled={i === 0} onClick={() => move(i, -1)} className="disabled:opacity-20 hover:text-slate-800 text-xs">↑</button>
                    <button type="button" disabled={i === f.items.length - 1} onClick={() => move(i, 1)} className="disabled:opacity-20 hover:text-slate-800 text-xs">↓</button>
                    <button type="button" onClick={() => setF({ ...f, items: f.items.filter((_, j) => j !== i) })} className="hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="ghost" size="sm" className="mt-2 h-8 text-xs" onClick={() => setF({ ...f, items: [...f.items, blank()] })} data-testid="tpl-add-item"><Plus className="h-3.5 w-3.5 mr-1" /> Add task</Button>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={busy || !f.project_type} data-testid="tpl-submit-button">{busy ? "Saving…" : "Save template"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const TemplatesTab = ({ types }) => {
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/checklist-templates").then((r) => setTemplates(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (t) => { if (!window.confirm(`Delete template "${t.name}"? Projects that already used it are not affected.`)) return; try { await api.delete(`/checklist-templates/${t.id}`); toast.success("Template deleted"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const groups = [...new Set(templates.map((t) => t.project_type))].sort();
  return (
    <div className="space-y-4" data-testid="templates-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Reusable execution checklists by project type. Apply them when activating a project or from the Checklist tab.</p>
        <Button className="bg-teal-600 hover:bg-teal-700" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-template-button"><Plus className="h-4 w-4 mr-1" /> New template</Button>
      </div>
      {templates.length === 0 && <div className="text-sm text-slate-400">No templates yet.</div>}
      {groups.map((g) => (
        <Card key={g} title={g} testid={`template-group-${g}`}>
          {templates.filter((t) => t.project_type === g).map((t) => (
            <div key={t.id} className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-100 last:border-0" data-testid={`template-row-${t.id}`}>
              <div>
                <div className="text-sm font-medium text-slate-800">{t.name} <span className="text-xs font-normal text-slate-500">· {t.items.length} tasks</span></div>
                <div className="text-xs text-slate-500 mt-0.5">{t.items.map((i) => i.title).join(" → ")}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditing(t); setOpen(true); }} data-testid={`edit-template-${t.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600" onClick={() => remove(t)} data-testid={`delete-template-${t.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </Card>
      ))}
      <TemplateDialog open={open} onOpenChange={setOpen} template={editing} types={types} onSaved={load} />
    </div>
  );
};
